import { createModule } from 'terrific-singleton';
import { TerrificSpec } from 'terrific-singleton/terrific-module';
import * as ProductCompareHelper from '@project/shared/utils/product-compare/js/product-compare';
import { onBreakpointChange } from '@project/shared/utils/breakpoints/js/breakpoints';

const classes = {
	headline: 'js-o-product-compare__headline',
	removebutton: 'js-o-product-compare__remove',
	backbutton: 'js-o-product-compare__back',
	productname: 'js-o-product-compare__productname',
	table: 'js-o-product-compare__table',
	tablerow: 'js-o-product-compare__table-row',
	tabletop: 'js-o-product-compare__table-top',
	fixedState: 'state-o-product-compare--fixed',
	tableHidden: 'state-o-product-compare--table-is-hidden',
};

@createModule('ProductCompare')
/**
 * product-compare module implementation.
 *
 * @author Lars-Olof Kreim <lars-olof.kreim@namics.com>
 */
export class ProductCompare extends TerrificSpec {
	headlineSingleText: string;
	headlineMultiText: string;
	animationFrameId: number;
	curScrollTop: number;
	curScrollLeft: number;
	isFixed: boolean;
	topThreshold: number = 20;
	$ctx: JQuery;

	/**
	 * start method, called on terrific bootstap
	 *
	 * @param {Object} resolve  function to call at the end of component start
	 * @returns {void}
	 */
	start(resolve: Function) {
		this.$ctx = $(this._ctx);
		const $table = this.$ctx.find(`.${classes.table}`);
		const $headline = this.$ctx.find(`.${classes.headline}`);
		this.topThreshold = $table.offset().top;
		this.headlineSingleText = $headline.data('single-headline');
		this.headlineMultiText = $headline.data('multi-headline');
		this.updateHeadline();
		this.$ctx.find(`.${classes.removebutton}`).on('click', this.onRemoveClick.bind(this));
		this.$ctx.find(`.${classes.backbutton}`).on('click', () => {
			window.history.back();
		});
		$(window).on('scroll', this.onScroll.bind(this));

		const $products = $(`.${classes.productname}`, this.$ctx);
		this.$ctx.toggleClass(classes.tableHidden, $products.length <= 0);

		onBreakpointChange(() => (this.topThreshold = $table.offset().top));

		resolve();
	}
	/**
	 * stop method, called on terrific stop
	 *
	 * @returns {void}
	 */
	stop() {}

	/**
	 * method updates headline and sets count of products
	 *
	 * @returns {void}
	 */
	updateHeadline() {
		const $headline = this.$ctx.find(`.${classes.headline}`);
		const count = this.$ctx.find(`.${classes.productname}`).length;
		if (count === 1) {
			$headline.text(this.headlineSingleText);
		} else {
			$headline.text(this.headlineMultiText.replace(/\{0\}/, `${count}`));
		}
	}

	/**
	 * method handels remove click and therefor removes product from list and cookie
	 *
	 * @param {Object} ev  eventObject
	 * @returns {void}
	 */
	onRemoveClick(ev: Event) {
		ev.preventDefault();
		const $el = $(ev.currentTarget).closest(`.${classes.productname}`);
		ProductCompareHelper.removeProduct($el.data('prod-id'));
		this.$ctx.toggleClass(classes.tableHidden, $el.siblings().length <= 1);

		if ($el.siblings().length > 0) {
			this.$ctx.find(`.${classes.tablerow} > td:nth-child(${$el.index() + 1})`).remove();
		}

		this.updateHeadline();
	}

	/**
	 * method is called on scroll and debounces by requestAnimationFrame
	 *
	 * @returns {void}
	 */
	onScroll() {
		if (this.animationFrameId > 0) {
			window.cancelAnimationFrame(this.animationFrameId);
		}
		this.animationFrameId = window.requestAnimationFrame(() => {
			this.animationFrameId = 0;
			this.handleScrolling();
		});
	}

	/**
	 * method handles scroll position and calls corresponding methods if scrollposition has changed till last processing
	 *
	 * @returns {void}
	 */
	handleScrolling() {
		const scrollTop = window.scrollY;
		const scrollLeft = window.scrollX;

		if (scrollTop !== this.curScrollTop) {
			this.curScrollTop = scrollTop;
			this.handleScrollTop();
		}
		if (this.isFixed && scrollLeft !== this.curScrollLeft) {
			this.curScrollLeft = scrollLeft;
			this.handleScrollLeft();
		}
	}

	/**
	 * method handles scroll top position and sets tableheader fix when threshold was crossed and viceversa
	 *
	 * @returns {void}
	 */
	handleScrollTop() {
		const $table = this.$ctx.find(`.${classes.table}`);
		const $topRow = $table.find(`.${classes.tabletop}`);
		if (this.curScrollTop > this.topThreshold) {
			if (!this.isFixed) {
				this.isFixed = true;
				$table.css('padding-top', `${$topRow.height()}px`);
				this.$ctx.addClass(classes.fixedState);
				this.handleScrollLeft();
			}
		} else if (this.isFixed) {
			this.isFixed = false;
			$table.css('padding-top', '0');
			this.$ctx.removeClass(classes.fixedState);
			$topRow.css('transform', 'translateX(0)');
		}
	}

	/**
	 * method handles scroll left position and moves the tableheader accordingly
	 *
	 * @returns {void}
	 */
	handleScrollLeft() {
		const $table = this.$ctx.find(`.${classes.table}`);
		const $topRow = $table.find(`.${classes.tabletop}`);
		$topRow.css('transform', `translateX(-${this.curScrollLeft}px)`);
	}
}
